import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import Navbar from "../components/Navbar";
import BillingModal from "../components/BillingModal";
import { useAuth } from "../context/AuthContext";
import { Receipt, Download, AlertTriangle, RefreshCw, FileText, Edit3,
         Crown, Settings2, Repeat, XCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import Footer from "../components/Footer";

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return s; }
};

const fmtMoney = (amt, cur) => {
  if (amt == null) return "—";
  try {
    return new Intl.NumberFormat("pt-PT", { style: "currency", currency: (cur || "eur").toUpperCase() }).format(Number(amt));
  } catch { return `${amt} ${cur || ""}`; }
};

const pkgLabel = (pkg_id) => {
  switch (pkg_id) {
    case "annual":        return "Subscrição Anual";
    case "monthly":       return "Subscrição Mensal";
    case "one_off_year":  return "1 ano";
    case "one_off_month": return "1 mês";
    default:              return pkg_id || "—";
  }
};

const Billing = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelComment, setCancelComment] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, iRes, sRes] = await Promise.all([
        client.get("/billing-profile"),
        client.get("/invoices"),
        client.get("/payments/subscription"),
      ]);
      setProfile(pRes.data.billing_profile);
      setInvoices(iRes.data.invoices || []);
      setTransactions(iRes.data.transactions || []);
      setSubscription(sRes.data || null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const openPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await client.post("/payments/portal", {
        return_url: window.location.origin + "/faturacao",
      });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível abrir o portal de gestão.");
      setOpeningPortal(false);
    }
  };

  const cancelSubscription = async () => {
    setCancelBusy(true);
    try {
      await client.post("/payments/cancel-subscription", {
        reason: cancelReason || null,
        comment: cancelComment.trim() || null,
      });
      toast.success("Cancelamento agendado. Mantens o acesso Premium até ao fim do ciclo já pago.");
      setCancelConfirmOpen(false);
      setCancelReason("");
      setCancelComment("");
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível cancelar agora.");
    } finally {
      setCancelBusy(false);
    }
  };

  const openCancelModal = () => {
    setCancelReason("");
    setCancelComment("");
    setCancelConfirmOpen(true);
  };

  const resumeSubscription = async () => {
    setResumeBusy(true);
    try {
      await client.post("/payments/resume-subscription");
      toast.success("Subscrição reactivada — vais continuar Premium.");
      await fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Não foi possível reactivar.");
    } finally {
      setResumeBusy(false);
    }
  };

  const downloadPdf = async (inv, kind = "receipt") => {
    // kind: "receipt" (RC) or "invoice" (FT)
    const cachedKey = kind === "invoice" ? "invoice_pdf_url" : "receipt_pdf_url";
    let url = inv[cachedKey] || (kind === "receipt" ? inv.pdf_url : null);
    if (!url) {
      try {
        const r = await client.post(`/invoices/${inv.id}/refresh-pdf`, null, { params: { kind } });
        url = r.data.pdf_url;
        // Optimistic local update
        setInvoices((prev) => prev.map((it) => it.id === inv.id ? { ...it, [cachedKey]: url } : it));
      } catch (err) {
        toast.error(err?.response?.data?.detail || `Não foi possível obter o PDF d${kind === "invoice" ? "a Fatura" : "o Recibo"}.`);
        return;
      }
    }
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  const backfillInvoice = async (inv) => {
    try {
      const r = await client.post(`/invoices/${inv.id}/backfill`);
      const merged = r.data.invoice || {};
      setInvoices((prev) => prev.map((it) => it.id === inv.id ? { ...it, ...merged } : it));
      toast.success("Documentos atualizados.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao atualizar documentos.");
    }
  };

  const retryInvoice = async (sessionId) => {
    try {
      await client.post(`/invoices/retry/${sessionId}`);
      toast.success("A re-emitir fatura — atualiza dentro de momentos.");
      setTimeout(fetchAll, 4000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao re-emitir.");
    }
  };

  // Transactions without an issued invoice (and that were paid)
  const pendingTx = transactions.filter(
    (t) => t.payment_status === "paid" && t.invoice_status !== "issued"
  );

  return (
    <div className="min-h-screen bg-[#0C0E12]" data-testid="billing-page">
      <Navbar />
      <main className="max-w-4xl mx-auto px-6 md:px-12 py-10">
        <div className="flex items-center gap-2">
          <Receipt className="w-4 h-4 text-[#C5A880]" />
          <span className="label-uppercase">Faturação</span>
        </div>
        <h1 className="font-display text-3xl md:text-5xl mt-3 mb-2">As tuas faturas</h1>
        <p className="text-[#A29F9B] mb-10 max-w-2xl">
          Encomendas, pagamentos e faturas-recibo emitidas pela Solfut, Lda.
        </p>

        {/* Subscription state */}
        {subscription && (subscription.has_subscription || subscription.is_premium) && (
          <section className="bg-gradient-to-br from-[#C5A880]/10 to-[#161920] border border-[#C5A880]/40 rounded-2xl p-6 mb-10"
            data-testid="subscription-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Crown className="w-4 h-4 text-[#C5A880]" />
                  <span className="label-uppercase">Subscrição Premium</span>
                </div>
                <h2 className="font-display text-2xl text-[#F5F2EE]">
                  {subscription.has_subscription
                    ? (subscription.subscription_interval === "year" ? "Subscrição Anual" : "Subscrição Mensal")
                    : "Acesso Premium"}
                </h2>
                <div className="text-sm text-[#A29F9B] mt-2 space-y-1">
                  {subscription.cancel_at_period_end ? (
                    <p className="text-[#E37B7B] inline-flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      Cancelamento agendado — acesso mantém-se até {fmtDate(subscription.premium_until)}.
                    </p>
                  ) : subscription.subscription_status === "cancelled" ? (
                    <p>Subscrição cancelada · acesso até {fmtDate(subscription.premium_until)}.</p>
                  ) : subscription.has_subscription ? (
                    <p className="inline-flex items-center gap-1.5">
                      <Repeat className="w-3.5 h-3.5 text-[#C5A880]" />
                      Renovação automática · próxima cobrança {fmtDate(subscription.premium_until)}.
                    </p>
                  ) : (
                    <>
                      <p className="inline-flex items-center gap-1.5">
                        <Crown className="w-3.5 h-3.5 text-[#C5A880]" />
                        Acesso Premium activo até {fmtDate(subscription.premium_until)}.
                      </p>
                      <p className="text-[#A29F9B] text-xs mt-1">
                        Sem renovação automática — não vai haver nova cobrança.
                        {subscription.is_premium && " O acesso simplesmente expira na data acima."}
                      </p>
                    </>
                  )}
                </div>
              </div>
              {subscription.has_customer && (
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Resume button — only when cancellation is pending */}
                  {subscription.cancel_at_period_end && subscription.has_subscription && (
                    <button onClick={resumeSubscription} disabled={resumeBusy}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="resume-subscription">
                      <RotateCcw className="w-4 h-4" />
                      {resumeBusy ? "A reactivar..." : "Reactivar subscrição"}
                    </button>
                  )}
                  {/* Cancel button — only when active and not already scheduled */}
                  {!subscription.cancel_at_period_end
                    && subscription.subscription_status !== "cancelled"
                    && subscription.has_subscription && (
                    <button onClick={openCancelModal}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 transition text-sm"
                      data-testid="cancel-subscription">
                      <XCircle className="w-4 h-4" />
                      Cancelar subscrição
                    </button>
                  )}
                  <button onClick={openPortal} disabled={openingPortal}
                    className="btn-secondary inline-flex items-center gap-2"
                    data-testid="manage-subscription">
                    <Settings2 className="w-4 h-4" />
                    {openingPortal ? "A abrir..." : "Gerir subscrição"}
                  </button>
                </div>
              )}
            </div>
          </section>
        )}

        {/* Billing profile */}
        <section className="bg-[#161920] border border-white/5 rounded-2xl p-6 mb-10" data-testid="billing-profile-card">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
            <div>
              <span className="label-uppercase">Dados de faturação</span>
              <h2 className="font-display text-xl mt-1">Onde a fatura é emitida</h2>
            </div>
            <button onClick={()=>setEditOpen(true)} className="btn-secondary inline-flex items-center gap-2"
              data-testid="billing-edit">
              <Edit3 className="w-4 h-4" /> {profile ? "Editar" : "Adicionar"}
            </button>
          </div>
          {!profile ? (
            <p className="text-[#A29F9B] text-sm">
              Ainda não definiste os teus dados de faturação. Vais poder fazê-lo ao subscrever ou aqui.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-y-3 gap-x-6 text-sm">
              <Field label="Nome" value={profile.name} />
              <Field label="NIF" value={profile.nif || "—"} />
              <Field label="Morada" value={profile.address} />
              <Field label="Código postal" value={profile.postal_code} />
              <Field label="Localidade" value={profile.city} />
              <Field label="País" value={profile.country} />
              {profile.email && <Field label="Email da fatura" value={profile.email} />}
            </div>
          )}
        </section>

        {/* Pending / failed transactions */}
        {pendingTx.length > 0 && (
          <section className="bg-[#161920] border border-[#C5A880]/30 rounded-2xl p-6 mb-10" data-testid="pending-tx">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-[#C5A880]" />
              <span className="label-uppercase">Pagamento sem fatura emitida</span>
            </div>
            <p className="text-sm text-[#A29F9B] mb-4">
              Os pagamentos abaixo foram confirmados mas a fatura ainda não foi emitida.
              Podes re-tentar manualmente.
            </p>
            <div className="space-y-3">
              {pendingTx.map((t) => (
                <div key={t.session_id} className="flex items-center justify-between gap-4 flex-wrap p-3 rounded-xl bg-[#0C0E12] border border-white/5"
                  data-testid={`pending-${t.session_id.slice(0,12)}`}>
                  <div className="text-sm">
                    <div className="text-[#F5F2EE]">{pkgLabel(t.package_id)} — {fmtMoney(t.amount, t.currency)}</div>
                    <div className="text-xs text-[#A29F9B] mt-0.5">{fmtDate(t.created_at)}{t.invoice_error ? ` · ${t.invoice_error.substring(0, 80)}` : ""}</div>
                  </div>
                  <button onClick={()=>retryInvoice(t.session_id)} className="btn-secondary inline-flex items-center gap-2 text-xs">
                    <RefreshCw className="w-3.5 h-3.5" /> Re-emitir
                  </button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Invoices */}
        <section data-testid="invoices-section">
          <div className="flex items-center justify-between mb-4">
            <span className="label-uppercase">Histórico</span>
            <button onClick={fetchAll} className="text-xs text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-1"
              data-testid="invoices-refresh">
              <RefreshCw className="w-3 h-3" /> Atualizar
            </button>
          </div>

          {loading && <div className="text-[#A29F9B]">A carregar...</div>}

          {!loading && invoices.length === 0 && (
            <div className="bg-[#161920] border border-white/5 rounded-2xl p-12 text-center" data-testid="invoices-empty">
              <FileText className="w-8 h-8 text-[#C5A880] mx-auto mb-4" />
              <h3 className="font-display text-2xl mb-2">Sem faturas emitidas ainda</h3>
              <p className="text-[#A29F9B]">Após a primeira subscrição Premium, a fatura-recibo aparece aqui.</p>
            </div>
          )}

          {!loading && invoices.length > 0 && (
            <div className="space-y-3">
              {invoices.map((inv) => {
                const hasInvoice = !!(inv.moloni_invoice_id);
                const hasReceipt = !!(inv.moloni_receipt_id || inv.moloni_document_id);
                const invoiceLabel = inv.invoice_number || (hasInvoice ? "Fatura" : null);
                const receiptLabel = inv.receipt_number || inv.number || "Recibo";
                // Legacy rows (issued before v15) won't have moloni_invoice_id
                // populated explicitly — offer a "Atualizar documentos" button.
                const isLegacy = !inv.invoice_pdf_url && !inv.invoice_number && !inv.moloni_invoice_id;
                return (
                  <article key={inv.id} className="bg-[#161920] border border-white/5 rounded-2xl p-5 hover:border-white/15 transition-colors"
                    data-testid={`invoice-card-${inv.id}`}>
                    <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
                      <div className="min-w-0">
                        <div className="text-sm text-[#DCD7D2] mb-1.5 truncate">{inv.description || "Subscrição Soulmatch"}</div>
                        <div className="text-xs text-[#A29F9B]">{fmtDate(inv.issued_at)}</div>
                      </div>
                      <span className="font-display text-xl text-[#F5F2EE] shrink-0">{fmtMoney(inv.amount, inv.currency)}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {hasInvoice && invoiceLabel && (
                        <button onClick={()=>downloadPdf(inv, "invoice")}
                          className="btn-primary inline-flex items-center gap-2 text-sm"
                          data-testid={`invoice-download-ft-${inv.id}`}>
                          <FileText className="w-3.5 h-3.5" /> Fatura {invoiceLabel.replace(/^Fatura\s*/i, "")}
                        </button>
                      )}
                      {hasReceipt && (
                        <button onClick={()=>downloadPdf(inv, "receipt")}
                          className="btn-secondary inline-flex items-center gap-2 text-sm"
                          data-testid={`invoice-download-rc-${inv.id}`}>
                          <Download className="w-3.5 h-3.5" /> Recibo {receiptLabel.replace(/^Recibo\s*/i, "")}
                        </button>
                      )}
                      {isLegacy && (
                        <button onClick={()=>backfillInvoice(inv)}
                          className="text-xs text-[#A29F9B] hover:text-[#C5A880] inline-flex items-center gap-1 ml-auto"
                          data-testid={`invoice-backfill-${inv.id}`}
                          title="Algumas faturas antigas só guardaram o Recibo. Carrega aqui para obter também a Fatura.">
                          <RefreshCw className="w-3 h-3" /> Atualizar documentos
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <BillingModal
        open={editOpen}
        user={user}
        onClose={()=>setEditOpen(false)}
        onConfirm={()=>{ setEditOpen(false); fetchAll(); toast.success("Dados de faturação guardados."); }}
      />

      {/* Cancel confirmation modal */}
      {cancelConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => !cancelBusy && setCancelConfirmOpen(false)}
          data-testid="cancel-confirm-backdrop">
          <div className="bg-[#161920] border border-rose-500/30 rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="cancel-confirm-modal">
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-rose-400 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-display text-2xl text-[#F5F2EE]">Cancelar subscrição Premium?</h3>
                <p className="text-sm text-[#A29F9B] mt-2 leading-relaxed">
                  Agendamos o cancelamento para o fim do ciclo já pago. Mantens o
                  acesso Premium até <b className="text-[#F5F2EE]">{fmtDate(subscription?.premium_until)}</b>{" "}
                  — sem mais cobranças automáticas. Podes reactivar a qualquer
                  momento antes dessa data.
                </p>
              </div>
            </div>

            {/* Reason survey — optional but helps us improve */}
            <div className="mt-4 border-t border-white/5 pt-4">
              <p className="text-xs uppercase tracking-widest text-[#C5A880] mb-3">
                O que te fez querer cancelar? <span className="text-[#A29F9B] normal-case tracking-normal">(opcional)</span>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4" data-testid="cancel-reason-options">
                {[
                  { k: "price",      label: "Preço demasiado alto" },
                  { k: "no_matches", label: "Não encontrei ninguém compatível" },
                  { k: "life_pause", label: "Pausa na minha vida agora" },
                  { k: "not_using",  label: "Não estou a usar o suficiente" },
                  { k: "other",      label: "Outro motivo" },
                ].map((r) => {
                  const selected = cancelReason === r.k;
                  return (
                    <button key={r.k} type="button"
                      onClick={() => setCancelReason(selected ? "" : r.k)}
                      data-testid={`cancel-reason-${r.k}`}
                      className={`text-left text-sm px-3 py-2 rounded-lg border transition ${
                        selected
                          ? "border-[#C5A880] bg-[#C5A880]/15 text-[#F5F2EE]"
                          : "border-white/10 bg-[#0C0E12] text-[#DCD7D2] hover:border-white/25"
                      }`}>
                      {r.label}
                    </button>
                  );
                })}
              </div>
              <label className="block text-xs text-[#A29F9B] mb-2" htmlFor="cancel-comment-input">
                Algum detalhe que queiras partilhar? <span className="italic">(opcional, fica confidencial)</span>
              </label>
              <textarea id="cancel-comment-input"
                value={cancelComment}
                onChange={(e) => setCancelComment(e.target.value)}
                placeholder="Ex.: faltou-me ver mais perfis fora de Lisboa..."
                rows={3}
                maxLength={1000}
                className="w-full bg-[#0C0E12] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[#C5A880]/50 focus:outline-none"
                data-testid="cancel-comment-input" />
              <p className="text-[10px] text-[#A29F9B] mt-1 text-right">{cancelComment.length}/1000</p>
            </div>

            <div className="flex items-center justify-end gap-2 mt-6">
              <button onClick={() => setCancelConfirmOpen(false)} disabled={cancelBusy}
                className="btn-secondary text-sm"
                data-testid="cancel-confirm-keep">
                Manter subscrição
              </button>
              <button onClick={cancelSubscription} disabled={cancelBusy}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-rose-500/90 hover:bg-rose-500 text-white transition text-sm disabled:opacity-60"
                data-testid="cancel-confirm-submit">
                <XCircle className="w-4 h-4" />
                {cancelBusy ? "A cancelar..." : "Sim, cancelar"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-xs text-[#A29F9B] uppercase tracking-wider">{label}</div>
    <div className="text-sm text-[#F5F2EE] mt-0.5">{value || "—"}</div>
  </div>
);

export default Billing;
